import { lineColors } from "../constants";
import type { IComponentFaultFrequencyApi } from "../interfaces";

export function getLineColor(colorIndex: number): string {
  return lineColors[colorIndex % lineColors.length];
}

export function createStableColorAssigner() {
  const colorsByKey = new Map<string, string>();
  let nextColorIndex = 0;

  return {
    get(stableKey: string): string {
      const key = String(stableKey);
      if (!colorsByKey.has(key)) {
        colorsByKey.set(key, getLineColor(nextColorIndex));
        nextColorIndex += 1;
      }
      return colorsByKey.get(key)!;
    },
    reset(): void {
      colorsByKey.clear();
      nextColorIndex = 0;
    },
  };
}

export function getFaultFrequencySelectionKey(faultFrequency: IComponentFaultFrequencyApi): string {
  const prefix = faultFrequency.idTrainComponent == null ? "" : `train-component-${faultFrequency.idTrainComponent}-`;

  return `${prefix}fault-code-${faultFrequency.code}`;
}

export function buildFaultFrequencyColorMap(faultFrequencies: IComponentFaultFrequencyApi[]): Map<string, string> {
  const assigner = createStableColorAssigner();
  const map = new Map<string, string>();

  for (const f of faultFrequencies) {
    if (f.isVisible === false) {
      continue;
    }

    const selectionKey = getFaultFrequencySelectionKey(f);
    map.set(selectionKey, assigner.get(selectionKey));

    if (!f.lines?.length && f.freqCode) {
      map.set(String(f.freqCode), assigner.get(selectionKey));
    }
  }

  for (const f of faultFrequencies) {
    if (f.isVisible === false) {
      continue;
    }

    if (f.lines?.length) {
      for (const ln of f.lines) {
        map.set(ln.lineKey, assigner.get(getFaultFrequencySelectionKey(f)));
      }
    }
  }

  return map;
}

export function filterValidFaultLineKeys(
  selectedKeys: string[],
  faultFrequencies: IComponentFaultFrequencyApi[],
): string[] {
  if (!selectedKeys.length) {
    return selectedKeys;
  }

  const validKeys = new Set<string>();

  for (const f of faultFrequencies) {
    validKeys.add(getFaultFrequencySelectionKey(f));

    if (f.lines?.length) {
      for (const ln of f.lines) {
        validKeys.add(ln.lineKey);
      }
    } else if (f.freqCode) {
      validKeys.add(f.freqCode);
    }
  }

  return selectedKeys.filter(key => validKeys.has(key));
}
